<template>
  <form @submit.prevent="handleSubmit">
    <a @click.prevent="$router.go(-1)" href="#" class="link-back"><span class="icon-ico-arrow-left"></span> Edit
      Role</a>
    <div class="white-box p30">
      <div class="row">
        <div class="col-xl-6 col-sm-6">
          <FieldTextInput name="Role Name" rules="required" v-model="data.name" />
        </div>
        <div class="col-xl-6 col-sm-6">
          <FieldColorPicker name="Color" rules="required" v-model="data.color" />
        </div>
        <div class="col-xl-6 col-sm-6">
          <FieldAsyncSelect name="Superior" v-model="data.superior" :options-handlers="getRoles" />
        </div>
        <div class="col-xl-12">
          <div class="form-group">
            <div class="text-black mb15">Access</div>
            <div class="row">
              <div v-for="(permission, parent) in permissions" :key="parent" class="col-md-6">
                <div class="mb15 ft16 font-500" style="text-transform: uppercase;">{{ parent }}</div>
                <div class="form-check mb15 ml15" v-for="p in permission" :key="p">
                  <input class="form-check-input" type="checkbox" :value="p" :id="p" v-model="data.permissions">
                  <label class="form-check-label" :for="p">
                    {{ p }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-center">
        <template v-if="isEditPage">
          <button v-if="hideDeleteBtn" @click="deleteRole" class="btn btn-border-red mx-2" type="button">Delete</button>
          <button v-if="hideEditBtn" class="btn btn-orange mx-2" type="submit">Update</button>
        </template>
        <template v-else>
          <button type="submit" class="btn btn-orange">Save</button>
        </template>
      </div>
    </div>
  </form>
</template>

<script>
import FieldAsyncSelect from '@/components/field/AsyncSelect.vue';
import FieldColorPicker from '@/components/field/ColorPicker.vue';
import FieldTextInput from '@/components/field/TextInput.vue';
import pluralize from 'pluralize';

import axios from '@/axios';
import { API_ENDPOINT } from '@/constants/api-endpoint.js';
import { showErrorMessage } from '@/utils';
import { getOptions } from '@/utils/fetch.js';

export default {
  components: {
    FieldTextInput,
    FieldColorPicker,
    FieldAsyncSelect
  },
  data() {
    return {
      data: {
        name: '',
        color: '#000000',
        permissions: [],
        superior: ''
      },
      detail: null
    }
  },
  methods: {
    getRoles({ query }) {
      return getOptions(API_ENDPOINT.ROLES, {
        key: query
      })
    },

    // Map specific entities to their parent grouping
    resolveCustomEntity(entity) {
      const customMappings = {
        "role employee": "role management",
        "customer address": "customer address management",
        "customer address status": "customer address management",
        "temporary solution on feedback and complaint": "feedback and complaint",
        "pic": "pic management",
        "quantity adjustment": "target",
        "technical": "technical management",
        "label customer": "label management",
        "employee": "employee management"
      };

      return customMappings[entity] || entity;
    },

    normalizeEntity(entity) {
      return pluralize.singular(entity.toLowerCase().trim());
    },

    extractParts(str) {
      const actionRegex = /^(View All|View My|View|Create|Edit|Delete|Close|Revision|Approve|Request Close|Comment|Cancel|Active\/Inactive)/;
      const match = str.match(actionRegex);
      const action = match ? match[0] : "Other";
      const rawEntity = str.replace(action, "").trim();
      const normalizedEntity = this.normalizeEntity(rawEntity);
      const finalEntity = this.resolveCustomEntity(normalizedEntity);
      return { action, entity: finalEntity, original: str };
    },

    deleteRole() {
      if (confirm('Are you sure you want to delete this role?')) {
        this.$store.dispatch('toggleLoadingProgress', true)
        axios.delete(`/roles/${this.$route.params.id}`)
          .then(() => {
            this.$notify({ type: 'success', title: 'Success', text: 'Success delete role' })
            this.$router.push({
              name: 'RoleManagement'
            })
          }).catch(error => {
            showErrorMessage(error)
          }).finally(() => {
            this.$store.dispatch('toggleLoadingProgress', false)
          })
      }
    },

    async handleSubmit() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) return

      const action = this.isEditPage ? 'updating' : 'creating';

      this.$store.dispatch('toggleLoadingProgress', true);

      try {
        const url = this.isEditPage ? `/roles/${this.$route.params.id}` : '/roles';
        const method = this.isEditPage ? 'put' : 'post';

        await axios[method](url, {
          ...this.data,
          superior: this.data?.superior?.value
        });

        this.$notify({
          type: 'success',
          title: 'Success',
          text: `Success ${action} role`
        });

        this.$router.push({ name: 'RoleManagement' });

      } catch (error) {
        showErrorMessage(error);
      } finally {
        this.$store.dispatch('toggleLoadingProgress', false);
      }
    }
  },
  computed: {
    permissions() {
      if (!this.$store.state.permissions) return {}
      const grouped = {};
      this.$store.state.permissions.forEach((perm) => {
        const { entity, original } = this.extractParts(perm);
        if (!grouped[entity]) {
          grouped[entity] = [];
        }
        grouped[entity].push(original); // keep original permission name
      });
      return grouped;
    },
    AppActiveUser() { return this.$store.state.AppActiveUser },
    hideDeleteBtn() {
      return (this.AppActiveUser.permissions.includes('Delete Role Employee') && this.detail?.role?.name !== 'Super Admin') && this.detail?.role?.name !== this.AppActiveUser.role
    },
    hideEditBtn() {
      return this.AppActiveUser.permissions.includes('Edit Role Employee') && (this.AppActiveUser.role === 'Super Admin' ? true : this.detail?.role?.name !== this.AppActiveUser.role && this.detail?.role?.name !== 'Super Admin')
    },
    isEditPage() {
      return this.$route.name === 'EditRoleManagement'
    }
  },
  async created() {
    if (!this.isEditPage) return
    try {
      this.$store.dispatch('toggleLoadingProgress', true)
      const { data } = await axios.get(`/roles/${this.$route.params.id}`)
      this.detail = data?.data
      this.data = {
        color: data?.data?.color,
        name: data?.data?.name,
        superior: data?.data?.superior ? {
          text: data?.data?.superior_name,
          value: data?.data?.superior
        } : null,
        permissions: data?.data?.permissions.map(({ name }) => name),
      }
    } catch (error) {
      showErrorMessage(error)
    } finally {
      this.$store.dispatch('toggleLoadingProgress', false)
    }
  },
}
</script>
